import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '../state/UserContext';
import { useApi } from '../state/ApiContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  retainAfterSessionExit?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  retainAfterSessionExit = false,
}) => {
  const { isLoggedIn, fetchUserData } = useUserContext();
  const { notificationsApi } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(isLoggedIn);
  const observedAuthenticatedSession = useRef(isLoggedIn);
  const pendingRead = useRef<string | null>(null);

  useEffect(() => {
    const checkAuthentication = async () => {
      if (isLoggedIn) {
        observedAuthenticatedSession.current = true;
        setIsAuthenticated(true);
      } else if (
        retainAfterSessionExit &&
        observedAuthenticatedSession.current
      ) {
        setIsAuthenticated(true);
      } else {
        // Try to fetch user data from API
        const userData = await fetchUserData();
        setIsAuthenticated(!!userData);
        if (userData) observedAuthenticatedSession.current = true;
      }
      setIsChecking(false);
    };

    checkAuthentication();
  }, [isLoggedIn, fetchUserData, retainAfterSessionExit]);

  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    const parameters = new URLSearchParams(location.search);
    const clickToken = parameters.get('pushClickToken');
    if (clickToken === null) {
      pendingRead.current = null;
      return;
    }
    if (pendingRead.current === clickToken) return;
    pendingRead.current = clickToken;
    const validToken =
      clickToken.length <= 256 &&
      /^v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/u.test(clickToken);
    const next = new URLSearchParams(location.search);
    next.delete('pushClickToken');
    // Consume the continuation before starting I/O. A delayed response can
    // then never navigate over a newer route selected by the user.
    navigate(
      {
        pathname: location.pathname,
        search: next.toString() ? `?${next.toString()}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
    if (!validToken) return;
    void notificationsApi
      .markWebPushClickViewed({ token: clickToken })
      .catch(() => undefined);
  }, [
    isAuthenticated,
    isChecking,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    notificationsApi,
  ]);

  // Show nothing while checking authentication
  if (isChecking) {
    return null;
  }

  if (!isAuthenticated) {
    // Redirect to login page with the current path as redirectUrl
    const currentPath = location.pathname + location.search + location.hash;
    return (
      <Navigate
        to={`/login?redirectUrl=${encodeURIComponent(currentPath)}`}
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
